import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { message as antMessage, Form, Steps, Row, Button } from 'antd';
import { CheckOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import RegisterInput from './RegisterInput';
import { isValidZipcode } from '../../utils/zipcode';

const RegisterForm = ({
  switchForm,
  current,
  setCurrent,
  mail,
  insurer,
  setInsurer,
  setEmail,
  email,
  setMail
}) => {
  // Variable & states
  const { dispatchAPI } = useAuthContext();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [enums, setEnums] = useState({});
  const [bics, setBics] = useState([]);
  const [oriasStatus, setOriasStatus] = useState();
  const [siretStatus, setSiretStatus] = useState();
  const [cities, setCities] = useState();
  const [cni, setCNI] = useState([]);
  const [kbis, setKbis] = useState([]);
  const [orias, setOrias] = useState([]);
  const [rib, setRib] = useState([]);
  const { message } = useErrorMessage();
  const [form] = Form.useForm();
  const [validateRegister, setValidateRegister] = useState(true);
  const [errorGetCities, setErrorGetCities] = useState(false);

  const { Step } = Steps;

  const steps = [
    { title: 'Étape 1' },
    { title: 'Étape 2' },
    { title: 'Étape 3' }
  ];

  const getInsurerWithMail = async () => {
    try {
      const data = await dispatchAPI('GET', {
        url: `/register/mail?mail=${email}`
      });
      setInsurer(data.data);
    } catch (e) {
      if (e.response) message.error(t('login.wrongDetails'));
    }
  };

  const printConvention = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/register/download?id=${insurer?._id}`,
        responseType: 'blob'
      });
      const blob = new Blob([data], {
        contentType: 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Convention de courtage.pdf`;
      a.click();
      antMessage.success(t('export.messages.success'));
      setValidateRegister(false);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const registerUser = async (values) => {
    setIsLoading(true);
    try {
      if (!siretStatus && current === 1) {
        antMessage.error(t('login.warning_siret'));
        setIsLoading(false);
      } else {
        const formData = new FormData();
        kbis.forEach((f) => formData.append('KBIS', f));
        orias.forEach((f) => formData.append('ORIAS', f));
        rib.forEach((f) => formData.append('RIB', f));
        cni.forEach((f) => formData.append('CNI', f));
        formData.append('values', JSON.stringify(values));
        formData.append('current', JSON.stringify(current));
        formData.append('email', JSON.stringify(email || mail));
        const response = await dispatchAPI('REGISTER', formData);
        if (response.status === 200) {
          antMessage.success(t(`login.registrationSuccess_${current}`));
        }
        if (current !== 2) {
          setCurrent(current + 1);
          document
            .getElementById('mainContent')
            .scrollTo({ behavior: 'smooth', top: 0 });
        } else {
          switchForm('login');
        }
        setIsLoading(false);
      }
    } catch (e) {
      if (e.response) {
        if (e.response.status === 409) {
          antMessage.error(t('login.email_in_db'));
        } else {
          antMessage.error(t('login.wrongDetails'));
        }
        setIsLoading(false);
      }
    }
  };

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/register/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getBics = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: 'bics' });
      setBics(data.map((b) => b.code));
    } catch (e) {
      setBics([]);
      message(e);
    }
  };

  // eslint-disable-next-line consistent-return
  const getCity = async (zipCode) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/zipcode/${zipCode}`
      });
      setErrorGetCities(false);
      const formattedCities = data.map((city) => ({
        ...city,
        label: `${city.name} (${city.zipcode})`
      }));
      return formattedCities;
    } catch (e) {
      if (e.response) {
        message(e.response.status);
      }
      const isNotFoundError = e.response?.status === 404;
      setErrorGetCities(!isNotFoundError);
      return [];
    }
  };

  const handlePostalCodeChange = async (event) => {
    const { value } = event.target;

    if (isValidZipcode(value)) {
      const cities = await getCity(value);
      setCities(cities);

      if (cities?.length > 0) {
        form.setFieldValue(['address', 'city'], cities[0].name);
      } else {
        form.setFieldValue(['address', 'city'], null);
      }
    }
  };

  const getSiretInfo = async (code) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/${code}`
      });
      if (data.header) {
        antMessage.success(t('login.known_siren_siret'));
        setSiretStatus(true);
        const {
          numeroVoieEtablissement,
          typeVoieEtablissement,
          libelleVoieEtablissement,
          codePostalEtablissement,
          complementAdresseEtablissement
        } = data.etablissement.adresseEtablissement;
        const {
          denominationUniteLegale,
          prenomUsuelUniteLegale,
          nomUniteLegale,
          sexeUniteLegale
        } = data.etablissement.uniteLegale;
        const { siren, nic } = data.etablissement;
        let tmpGender;
        if (sexeUniteLegale === 'M') {
          tmpGender = 'MISTER';
        }
        if (sexeUniteLegale === 'F') {
          tmpGender = 'MADAME';
        }
        let tmpStreet = libelleVoieEtablissement;
        if (typeVoieEtablissement) {
          tmpStreet = `${typeVoieEtablissement} ${libelleVoieEtablissement}`;
        }

        if (prenomUsuelUniteLegale && nomUniteLegale && tmpGender) {
          form.setFieldsValue({
            owner_first_name: prenomUsuelUniteLegale,
            owner_last_name: nomUniteLegale,
            owner_gender: tmpGender
          });
        }
        form.setFieldsValue({
          name: denominationUniteLegale || nomUniteLegale,
          address: {
            number: numeroVoieEtablissement,
            street: tmpStreet,
            postal_code: codePostalEtablissement,
            additionnal: complementAdresseEtablissement
          }
        });
        if (form.getFieldValue('siret_number').length < 14) {
          form.setFieldsValue({
            siret_number: `${siren}${nic}`
          });
        }
        await handlePostalCodeChange({
          target: { value: codePostalEtablissement }
        });
      }
    } catch (e) {
      if (e.response) message('unknown_siren_siret');
      setSiretStatus(false);
    }
  };

  const handleSiretChange = async (event) => {
    const { value } = event.target;
    if (value.length < 14) {
      setSiretStatus(false);
    }
    if (value === '99999999999999') {
      setSiretStatus(true);
    } else if (value.length === 14 || value.length === 9) {
      setIsLoading(true);
      await getSiretInfo(value);
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line consistent-return
  const getOriasInfo = async (number) => {
    try {
      const { data } = await dispatchAPI('GET', { url: `/orias/${number}` });
      const exists = data.find((element) => element.key === 'foundInRegistry')
        .value;
      const categoryInsurer = data.find(
        (element) => element.key === 'categoryName'
      ).value;
      form.setFieldsValue({ category_insurer: categoryInsurer });

      if (exists === 'false') {
        return setOriasStatus('INEXISTANT');
      }

      const siren = data.find((element) => element.key === 'siren');
      const categoryIndex = data.indexOf(
        data.find(
          (element) => element.key === 'categoryName' && element.value === 'COA'
        )
      );
      const status = data[categoryIndex + 1].value;
      setOriasStatus(status);

      if (siren) {
        form.setFieldsValue({
          siret_number: siren.value
        });
        await handleSiretChange({ target: { value: siren.value } });
      }
    } catch (e) {
      if (e.response) message('unknown_siren_siret');
    }
  };

  useEffect(() => {
    (async () => {
      if (current === 2 && email) {
        getInsurerWithMail();
      }
      await getEnums();
      await getBics();
    })();
    // eslint-disable-next-line
  }, [current]);

  const handleOriasChange = async (event) => {
    const { value } = event.target;

    if (value.length === 8) {
      await getOriasInfo(value);
    }
  };

  const getLayoutFromOriasStatus = (status) => {
    switch (status) {
      case 'INSCRIT':
        return {
          validateStatus: 'success',
          help: t('insurers.form.orias_success')
        };
      case 'SUPPRIME':
        return {
          validateStatus: 'error',
          help: t('insurers.form.orias_error')
        };
      case 'INEXISTANT':
        return {
          validateStatus: 'error',
          help: t('insurers.form.orias_not_found')
        };
      default:
        return null;
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setMail();
    setEmail();
    setCurrent(0);
    switchForm('login');
  };

  return (
    <>
      <Steps current={current} className="register-steps">
        {(steps || []).map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>
      <Form form={form} onFinish={registerUser} className="register-form">
        <RegisterInput
          t={t}
          handleSiretChange={handleSiretChange}
          handleOriasChange={handleOriasChange}
          handlePostalCodeChange={handlePostalCodeChange}
          cities={cities}
          getLayoutFromOriasStatus={getLayoutFromOriasStatus}
          isLoading={isLoading}
          enums={{ ...enums, bics }}
          oriasStatus={oriasStatus}
          siretStatus={siretStatus}
          setCNI={setCNI}
          setKbis={setKbis}
          setOrias={setOrias}
          setRib={setRib}
          switchForm={switchForm}
          current={current}
          setEmail={setEmail}
          email={email}
          insurer={insurer}
          validateRegister={validateRegister}
          printConvention={printConvention}
          orias={orias}
          cni={cni}
          kbis={kbis}
          rib={rib}
          errorGetCities={errorGetCities}
        />
        <Form.Item wrapperCol={24}>
          <Row style={{ marginTop: 24 }}>
            {current !== steps.length - 1 && (
              <Button type="primary" htmlType="submit" loading={isLoading}>
                <span>{`${t('buttons.next')}`}</span>
                <RightOutlined />
              </Button>
            )}
            {current === steps.length - 1 && (
              <>
                <Button
                  type="add"
                  htmlType="submit"
                  style={{ margin: 0 }}
                  disabled={validateRegister}
                  loading={isLoading}
                >
                  {t('buttons.validate_register')}
                  <CheckOutlined />
                </Button>
              </>
            )}
            <Button
              style={{ margin: '0 10px 0 0' }}
              type="link"
              danger
              onClick={handleCancel}
            >
              {current === 0
                ? `${t('buttons.back')} `
                : `${t('buttons.cancel')} `}
              <CloseOutlined />
            </Button>
          </Row>
        </Form.Item>
      </Form>
    </>
  );
};

RegisterForm.propTypes = {
  switchForm: PropTypes.func.isRequired,
  setCurrent: PropTypes.func.isRequired,
  current: PropTypes.number.isRequired,
  mail: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  insurer: PropTypes.shape({
    _id: PropTypes.string
  }),
  setInsurer: PropTypes.func.isRequired,
  setEmail: PropTypes.func.isRequired,
  setMail: PropTypes.func.isRequired
};

RegisterForm.defaultProps = {
  insurer: undefined
};

export default RegisterForm;
